/**
 * `/itineraries/discover` — PUBLIC itinerary browse (U8 / D5).
 *
 * Reachable by anonymous users (this route is NOT wrapped by
 * `OnboardingGuard` — see `App.tsx`). Data comes from
 * `trpc.itinerary.listPublic` (a `publicProcedure` + IP rate-limit).
 *
 * Information architecture (server contract — see `itinerary.listPublic`):
 *  - NO filter applied → the server returns `shelves` (fixed-priority,
 *    empty shelves already omitted). We render each shelf as a row of cards.
 *  - A region filter applied → the server returns `shelves: null`; we render
 *    the single ranked flat `items` grid.
 *  - Empty filtered result → "No itineraries match" + a working
 *    "Clear filters" control that returns to the shelved view WITHOUT a full
 *    reload (it just clears the region URL param → query refetches unfiltered).
 *
 * Page layout uses the exact mandated wrapper (no per-page `max-w-*`/
 * `mx-auto` — `Layout`'s `<main>` already provides navbar width). All three
 * wrappers (loading / error / content) are byte-identical so the page never
 * jumps width on load. No in-page "Back to…" links (repo rule #154).
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { PageContainer } from '../components/PageContainer';
import { Heading1, BodyText, Label } from '../components/ui/Typography';
import { Button } from '../components/ui/Button';
import {
  ItineraryCard,
  type ItineraryCardData,
} from '../components/itinerary/ItineraryCard';

const WRAPPER_CLASS = 'px-0 md:px-4 pt-2 pb-4';

const CardGrid = ({ items }: { items: ItineraryCardData[] }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
    {items.map((it) => (
      <ItineraryCard key={it.slug} itinerary={it} />
    ))}
  </div>
);

export const ItineraryBrowse = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const regionSlug = searchParams.get('region') ?? undefined;

  // `region.list` items are inferred as `unknown` (the router spreads a lean
  // doc through an `as unknown` _id cast). We only need name + slug for the
  // filter pills, so narrow with a local shape — same cast pattern as
  // `RegionSearch.tsx`.
  const regionsQuery = trpc.region.list.useQuery();
  const regionOptions = (regionsQuery.data ?? []) as Array<{
    name: string;
    slug: string;
  }>;
  const browseQuery = trpc.itinerary.listPublic.useQuery({
    limit: 60,
    offset: 0,
    regionSlug,
  });

  const itemsBySlug = useMemo(() => {
    const map = new Map<string, ItineraryCardData>();
    for (const it of browseQuery.data?.items ?? []) {
      map.set(it.slug, it);
    }
    return map;
  }, [browseQuery.data]);

  const setRegion = (slug: string | undefined) => {
    const next = new URLSearchParams(searchParams);
    if (slug) {
      next.set('region', slug);
    } else {
      next.delete('region');
    }
    // Updating the URL param refetches the query in place — no reload.
    setSearchParams(next);
  };

  if (browseQuery.isLoading) {
    return (
      <div className={WRAPPER_CLASS}>
        <PageContainer className="mt-2">
          <Heading1>Discover Itineraries</Heading1>
          <BodyText className="mt-4 text-gray-500">
            Loading itineraries…
          </BodyText>
        </PageContainer>
      </div>
    );
  }

  if (browseQuery.isError || !browseQuery.data) {
    return (
      <div className={WRAPPER_CLASS}>
        <PageContainer className="mt-2">
          <Heading1>Discover Itineraries</Heading1>
          <BodyText className="mt-4 text-gray-600">
            We couldn&rsquo;t load itineraries right now. Please try again
            shortly.
          </BodyText>
        </PageContainer>
      </div>
    );
  }

  const { shelves, items } = browseQuery.data;
  const filterApplied = Boolean(regionSlug);
  const isEmpty = items.length === 0;

  return (
    <div className={WRAPPER_CLASS}>
      <PageContainer className="mt-2">
        <Heading1>Discover Itineraries</Heading1>
        <BodyText className="mt-2 text-gray-600">
          Curated and community wine-country trips.
        </BodyText>

        {/* Region filter (opt-in). Selecting a region collapses shelves into
            a single ranked grid; "All regions" returns to the shelved view. */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Label as="span" className="text-gray-500 mr-1">
            Region
          </Label>
          <button
            type="button"
            onClick={() => setRegion(undefined)}
            className={
              !filterApplied
                ? 'inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-primary-700 text-white border border-primary-700'
                : 'inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 hover:border-primary-300'
            }
          >
            All regions
          </button>
          {regionOptions.map((r) => {
            const active = regionSlug === r.slug;
            return (
              <button
                key={r.slug}
                type="button"
                onClick={() => setRegion(r.slug)}
                className={
                  active
                    ? 'inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-primary-700 text-white border border-primary-700'
                    : 'inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 hover:border-primary-300'
                }
              >
                {r.name}
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {isEmpty ? (
            <div className="py-12 text-center">
              <Heading1 as="h2" className="text-2xl">
                No itineraries match
              </Heading1>
              <BodyText className="mt-2 text-gray-600">
                Try a different region or clear your filters.
              </BodyText>
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => setRegion(undefined)}
                >
                  Clear filters
                </Button>
              </div>
            </div>
          ) : filterApplied || !shelves ? (
            // Filtered → single ranked flat grid (shelves collapsed).
            <CardGrid items={items} />
          ) : (
            // Default → fixed-priority shelves (empty ones already omitted by
            // the server). Each shelf renders its slugs in server order.
            <div className="flex flex-col gap-8">
              {shelves.map((shelf) => {
                const shelfItems = shelf.slugs
                  .map((s) => itemsBySlug.get(s))
                  .filter((i): i is ItineraryCardData => Boolean(i));
                if (shelfItems.length === 0) return null;
                return (
                  <section key={shelf.key}>
                    <Heading1 as="h2" className="text-2xl mb-3">
                      {shelf.title}
                    </Heading1>
                    <CardGrid items={shelfItems} />
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </PageContainer>
    </div>
  );
};
